import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Check, BookOpen } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, authHeaders } from "../../lib/adminApi";

const CopyBtn = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={async () => { try { await navigator.clipboard.writeText(text); } catch {}; setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      data-testid={testid} className="p-1.5 rounded-lg text-slate-400 hover:text-[#FF5E1A] hover:bg-white" title="Copy">
      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const Code = ({ children, testid }) => (
  <pre data-testid={testid} className="bg-[#0F172A] text-slate-200 rounded-2xl p-4 text-xs overflow-x-auto whitespace-pre-wrap font-mono">{children}</pre>
);

export default function AdminApiDocs() {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState("");
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    (async () => {
      const [k, p] = await Promise.all([
        axios.get(`${API}/admin/api-key`, { headers: authHeaders() }),
        axios.get(`${API}/admin/onboarding/programs`, { headers: authHeaders() }),
      ]);
      setApiKey(k.data?.partner_api_key || "");
      setPrograms((p.data || []).filter((x) => x.active));
    })();
  }, []);

  const base = window.location.origin;
  const sampleProg = programs[0]?.id || "<PROGRAM_ID>";
  const curlExample = `curl -X POST ${base}/api/partner/register \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "name": "Rohan Shah",
    "phone": "919876543210",
    "email": "rohan@example.com",
    "company": "Rohan Estates",
    "city": "Pune",
    "program_id": "${sampleProg}"
  }'`;

  const responseExample = `{
  "cp_id": "f3ff8b44-...",
  "name": "Rohan Shah",
  "program_id": "${sampleProg}",
  "program_title": "...",
  "onboarding_link": "${base}/onboard/rohan-shah/Ab3xK9pQ",
  "is_new_cp": true,
  "invite_created": true
}`;

  return (
    <div className="min-h-screen bg-white/60">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3"><Logo /> <span className="font-heading font-bold text-lg">Partner API</span></div>
        <button onClick={() => navigate("/admin/dashboard")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#FF5E1A]"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-6" data-testid="api-docs">
        <div className="glass-strong rounded-2xl p-5">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#FF5E1A]" /> Registration → Onboarding Link API</h2>
          <p className="text-sm text-slate-600 mt-2">
            Call this API when a channel partner registers on the partner website, or when an existing CP clicks
            <b> "Start Onboarding"</b>. We create (or find) the CP, generate their personal onboarding link for the
            requested training program, and return the link — add it to your welcome message and store it for future communication.
          </p>
          <p className="text-sm text-slate-600 mt-2">
            <b>Safe to call repeatedly:</b> the same phone/email always returns the same link — no duplicates are created.
            New registrations are auto-assigned to the team member configured on the Channel Partners page.
          </p>
        </div>

        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Endpoint</div>
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm font-mono font-bold text-[#0F172A]">POST {base}/api/partner/register</code>
            <CopyBtn text={`${base}/api/partner/register`} testid="copy-endpoint" />
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-4 mb-2">Header (keep this key secret)</div>
          <div className="flex items-center justify-between gap-2">
            <code className="text-xs font-mono text-[#E04F00] break-all">X-API-Key: {apiKey}</code>
            <CopyBtn text={apiKey} testid="copy-api-key" />
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Request body (JSON)</div>
          <table className="w-full text-xs">
            <tbody>
              {[["name", "string", "required", "CP's full name"],
                ["phone", "string", "required*", "Mobile with country code, e.g. 919876543210"],
                ["email", "string", "required*", "Email (*phone or email — at least one)"],
                ["program_id", "string", "required", "Which training the link opens — see IDs below"],
                ["company", "string", "optional", "Agency / company name"],
                ["city", "string", "optional", ""],
                ["team_size", "number", "optional", ""],
                ["rera", "string", "optional", "RERA / certificate number"],
                ["address", "string", "optional", ""],
                ["pincode", "string", "optional", ""],
                ["notes", "string", "optional", ""]].map(([f, t, r, d]) => (
                <tr key={f} className="border-t border-slate-100">
                  <td className="py-1.5 font-mono font-semibold">{f}</td>
                  <td className="py-1.5 text-slate-500">{t}</td>
                  <td className="py-1.5">{r === "required" ? <span className="text-[#E04F00] font-semibold">{r}</span> : <span className="text-slate-500">{r}</span>}</td>
                  <td className="py-1.5 text-slate-500">{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="glass-strong rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Example (ready to run)</div>
            <CopyBtn text={curlExample} testid="copy-curl" />
          </div>
          <Code testid="api-curl-example">{curlExample}</Code>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-4 mb-2">Response</div>
          <Code testid="api-response-example">{responseExample}</Code>
        </div>

        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Active program IDs (use as program_id)</div>
          <table className="w-full text-xs" data-testid="api-program-ids">
            <tbody>
              {programs.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="py-1.5 font-semibold">{p.title}</td>
                  <td className="py-1.5 font-mono text-slate-500">{p.id}</td>
                  <td className="py-1.5 text-right"><CopyBtn text={p.id} testid={`copy-prog-${p.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="glass-strong rounded-2xl p-5 text-xs text-slate-600">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Errors & behaviour</div>
          <ul className="list-disc pl-4 flex flex-col gap-1">
            <li><code>401</code> — missing or wrong X-API-Key.</li>
            <li><code>400</code> — name missing, or both phone and email missing.</li>
            <li><code>404</code> — program_id not found or program inactive.</li>
            <li>Repeat calls with the same phone/email return the SAME link (<code>is_new_cp: false</code>, <code>invite_created: false</code>).</li>
            <li>Every person opening the link enters their own name, so a CP's whole team can use it and each person is tracked separately.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
